import {
  retailCodeGtin14,
  gtinMiddle12,
  scanGtinMiddle12,
  type ProductScanLike
} from '@/lib/scanning/retailCode';
import { matchUniqueSerialCaseInsensitive } from '@/lib/scanning/serialMatch';
import type { ZoneScanLookupResult } from '@/lib/shared-coms/ZoneScanner/useZoneScanLookup';

/**
 * Quarantine Cleanup's rules, without React or a camera: what is picked (a per-line count plus the
 * serialized units scanned), which line a scan or a typed code names, whether a zone scan is the
 * stop's zone, and what is sent on Complete. Nothing is written until Complete — the picks live
 * here, on the handheld — so skipping a zone, or leaving, moves nothing.
 */

export type CleanupZone = {
  id: string;
  name: string;
  zoneType?: string | null;
  pickSequence?: number | null;
};

export type CleanupUnit = {
  serializedItemId: string;
  serialNumber: string;
  gtin?: string | null;
};

export type CleanupLineProduct = {
  title?: string | null;
  fdaProduct?: { elevenDigitNdc?: string | null; deaSchedule?: string | null } | null;
  upcProduct?: { upc?: string | null } | null;
};

export type CleanupLine = {
  zoneInventoryBalanceId: string;
  /** The company's catalog title, else the FDA / UPC product name. */
  productName: string;
  ndc?: string | null;
  tenDigitNdc?: string | null;
  upc?: string | null;
  deaSchedule?: string | null;
  kind: string;
  lot?: string | null;
  expiration?: string | null;
  /** Everything pickable on the line: `bulkQuantity` plus the units. */
  quantity: number;
  /** Picked by count. */
  bulkQuantity: number;
  /** Picked by scanning each one (or all at once). */
  units: CleanupUnit[];
  matchKeys: string[];
};

export type CleanupStop = {
  rank: number;
  zone: CleanupZone;
  lines: CleanupLine[];
};

/** What one cleanup may hold — the same numbers `quarantineCleanupComplete` enforces. */
export type CleanupEventBudget = {
  maxEvents: number;
  perBulkLine: number;
  perUnit: number;
};

export type CleanupList = {
  locationId: string;
  pathConfigured: boolean;
  stops: CleanupStop[];
  totalQuantity: number;
  notes: string[];
  eventBudget: CleanupEventBudget;
};

export type LinePick = { bulk: number; unitIds: string[] };
/** Keyed by zoneInventoryBalanceId. */
export type CleanupPicks = Record<string, LinePick>;

const EMPTY_PICK: LinePick = { bulk: 0, unitIds: [] };

export const linePick = (picks: CleanupPicks, line: CleanupLine): LinePick =>
  picks[line.zoneInventoryBalanceId] || EMPTY_PICK;

export const pickedCount = (pick: LinePick | null | undefined): number =>
  (pick?.bulk || 0) + (pick?.unitIds?.length || 0);

export const isLineFullyPicked = (picks: CleanupPicks, line: CleanupLine): boolean =>
  pickedCount(linePick(picks, line)) >= line.quantity;

const withPick = (
  picks: CleanupPicks,
  line: CleanupLine,
  pick: LinePick
): CleanupPicks => {
  const next = { ...picks };
  if (pickedCount(pick) > 0) next[line.zoneInventoryBalanceId] = pick;
  else delete next[line.zoneInventoryBalanceId];
  return next;
};

/** Everything on the line: the full count and every serialized unit. */
export const pickAll = (picks: CleanupPicks, line: CleanupLine): CleanupPicks =>
  withPick(picks, line, {
    bulk: line.bulkQuantity,
    unitIds: line.units.map((unit) => unit.serializedItemId)
  });

export const clearLine = (picks: CleanupPicks, line: CleanupLine): CleanupPicks =>
  withPick(picks, line, EMPTY_PICK);

/** The counted part of a partial pick, held to 0…bulkQuantity. Non-numbers count as 0. */
export const setBulkCount = (
  picks: CleanupPicks,
  line: CleanupLine,
  value: number
): CleanupPicks => {
  const count = Number.isFinite(value) ? Math.trunc(value) : 0;
  const bulk = Math.min(Math.max(count, 0), line.bulkQuantity);
  return withPick(picks, line, { ...linePick(picks, line), bulk });
};

export const addUnit = (
  picks: CleanupPicks,
  line: CleanupLine,
  serializedItemId: string
): CleanupPicks => {
  const pick = linePick(picks, line);
  if (pick.unitIds.includes(serializedItemId)) return picks;
  return withPick(picks, line, { ...pick, unitIds: [...pick.unitIds, serializedItemId] });
};

export const stopPickedCount = (stop: CleanupStop, picks: CleanupPicks): number =>
  stop.lines.reduce((sum, line) => sum + pickedCount(linePick(picks, line)), 0);

export const totalPicked = (picks: CleanupPicks): number =>
  Object.values(picks).reduce((sum, pick) => sum + pickedCount(pick), 0);

export type PickListZone = {
  zone: CleanupZone;
  lines: { line: CleanupLine; picked: number }[];
  picked: number;
};

/** The "Pick List" modal: what has been picked so far, by zone, in walk order. */
export const pickListSummary = (
  stops: CleanupStop[],
  picks: CleanupPicks
): PickListZone[] =>
  stops
    .map((stop) => {
      const lines = stop.lines
        .map((line) => ({ line, picked: pickedCount(linePick(picks, line)) }))
        .filter(({ picked }) => picked > 0);
      return {
        zone: stop.zone,
        lines,
        picked: lines.reduce((sum, { picked }) => sum + picked, 0)
      };
    })
    .filter(({ picked }) => picked > 0);

/**
 * The inventory events these picks will write, counted the way the server counts them: one set per
 * line picked by count, one per serialized unit. (A case that follows its contents adds its own,
 * which only the server can see — it refuses then, and nothing moves.)
 */
export const cleanupEventCount = (
  picks: CleanupPicks,
  budget: CleanupEventBudget
): number =>
  Object.values(picks).reduce(
    (sum, pick) =>
      sum +
      (pick.bulk > 0 ? budget.perBulkLine : 0) +
      pick.unitIds.length * budget.perUnit,
    0
  );

/** Whether these picks are more than one cleanup can move. */
export const exceedsBudget = (
  picks: CleanupPicks,
  budget: CleanupEventBudget | null | undefined
) => !!budget && cleanupEventCount(picks, budget) > budget.maxEvents;

/** The `quarantineCleanupComplete` picks: only lines with something picked. */
export const completePicksInput = (picks: CleanupPicks) =>
  Object.entries(picks)
    .filter(([, pick]) => pickedCount(pick) > 0)
    .map(([zoneInventoryBalanceId, pick]) => ({
      zoneInventoryBalanceId,
      bulkQuantity: pick.bulk,
      serializedItemIds: pick.unitIds
    }));

// ───────────────────────────── the zone at each stop ─────────────────────────────

export type StopZoneOutcome =
  | { kind: 'accepted' }
  | { kind: 'lookupFailed'; error: unknown }
  | { kind: 'notFound' }
  | { kind: 'wrongZone'; scannedName: string };

/**
 * The picker must be at THIS stop's zone. A label that matches several zones is accepted when the
 * stop's zone is one of them — the picker is standing at it.
 */
export const classifyStopZoneLookup = (
  lookup: ZoneScanLookupResult,
  zone: CleanupZone
): StopZoneOutcome => {
  if (lookup.error) return { kind: 'lookupFailed', error: lookup.error };
  const resolved = [lookup.zone, ...(lookup.matches || [])].filter(
    (z): z is NonNullable<typeof z> => !!z?.id
  );
  if (resolved.some((z) => z.id === zone.id)) return { kind: 'accepted' };
  if (!resolved.length) return { kind: 'notFound' };
  return { kind: 'wrongZone', scannedName: resolved[0].name };
};

// ───────────────────────────── which line a product names ─────────────────────────────

export type CleanupScanMatch =
  /** A serialized unit of the line, by its serial number. */
  | { kind: 'unit'; line: CleanupLine; unit: CleanupUnit }
  /** The line's product (and lot, when the scan carries one): pick the line in full. */
  | { kind: 'line'; line: CleanupLine }
  /** Several lots of the product here and the scan does not say which. */
  | { kind: 'ambiguous'; lines: CleanupLine[] }
  /** A serialized unit of a product here, but not one quarantined in this zone. */
  | { kind: 'unitNotHere'; line: CleanupLine }
  | { kind: 'none' };

const digits = (value: unknown) => String(value ?? '').replace(/\D/g, '');

const byLot = (lines: CleanupLine[], lot: string | null | undefined) => {
  if (!lot || lines.length < 2) return lines;
  const narrowed = lines.filter((line) => (line.lot || '') === lot);
  return narrowed.length ? narrowed : lines;
};

const outcomeFor = (candidates: CleanupLine[]): CleanupScanMatch => {
  if (candidates.length === 1) return { kind: 'line', line: candidates[0] };
  if (candidates.length > 1) return { kind: 'ambiguous', lines: candidates };
  return { kind: 'none' };
};

/**
 * The line a camera / handheld scan names in this zone. A 2D code with a serial picks that unit
 * when it is one of the line's quarantined units; any other product barcode picks the line in full
 * (narrowed by the scan's lot when there are several lots).
 *
 * A serial is unique only within its GTIN, so a scan that names a product is matched against that
 * product's units alone — never against another product's unit that happens to share the serial.
 * Only a scan carrying no product code at all is matched by serial across the stop, and then only
 * when exactly one unit there has it.
 */
export const matchCleanupScan = (
  lines: CleanupLine[],
  scan: ProductScanLike
): CleanupScanMatch => {
  if (!scan) return { kind: 'none' };
  const middle12 = scanGtinMiddle12(scan);
  const products = middle12
    ? lines.filter((line) => (line.matchKeys || []).includes(middle12))
    : [];
  const serial = scan.serialNumber?.trim();
  if (serial) {
    const candidates = (middle12 ? products : lines).flatMap((line) =>
      line.units
        .map((unit) => ({ kind: 'unit' as const, line, unit }))
    );
    const hit = matchUniqueSerialCaseInsensitive(
      candidates,
      serial,
      (candidate) => candidate.unit.serialNumber
    );
    if (hit) return hit;
    // Ambiguous case-only collision: fall through to product/lot matching rather than guessing.
    const serializedLines = byLot(
      products.filter((line) => line.units.length > 0),
      scan.lot
    );
    if (serializedLines.length) return { kind: 'unitNotHere', line: serializedLines[0] };
  }
  return outcomeFor(byLot(products, scan.lot));
};

/**
 * The line a TYPED code names: a UPC / GTIN (as a scan would), else an NDC — 11 digits, or the 10
 * of the product's ten-digit NDC, hyphens ignored.
 */
export const matchTypedCode = (lines: CleanupLine[], code: string): CleanupScanMatch => {
  const text = (code || '').trim();
  if (!text) return { kind: 'none' };
  const middle12 = gtinMiddle12(retailCodeGtin14(digits(text)));
  if (middle12) {
    const byGtin = lines.filter((line) => (line.matchKeys || []).includes(middle12));
    if (byGtin.length) return outcomeFor(byGtin);
  }
  const typed = digits(text);
  return outcomeFor(
    lines.filter(
      (line) =>
        !!typed &&
        (digits(line.ndc) === typed ||
          digits(line.tenDigitNdc) === typed ||
          digits(line.upc) === typed)
    )
  );
};

export const lineProduct = (line: CleanupLine): CleanupLineProduct => {
  return {
    title: line.productName,
    fdaProduct:
      line.ndc || line.deaSchedule
        ? { elevenDigitNdc: line.ndc ?? null, deaSchedule: line.deaSchedule ?? null }
        : null,
    upcProduct: line.upc ? { upc: line.upc } : null
  };
};

export const unitsLabel = (count: number) => `${count} ${count === 1 ? 'unit' : 'units'}`;
